import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Download,
  Filter,
  ScrollText,
  Calendar,
} from 'lucide-react';
import { auditLogsApi, AuditLogEntry } from '@/api/endpoints';
import { toast } from '@/utils/toast';
import { format } from 'date-fns';

export const AuditLogsPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [level, setLevel] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params = {
    page,
    page_size: 20,
    search: search || undefined,
    action: action || undefined,
    resource_type: resourceType || undefined,
    level: level || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  };

  const { data: logsRes, isLoading } = useQuery({
    queryKey: ['audit-logs', params],
    queryFn: () => auditLogsApi.getLogs(params),
    staleTime: 30000,
  });

  const logs: AuditLogEntry[] = Array.isArray(logsRes?.data?.data) ? logsRes.data.data : [];
  const totalPages = logsRes?.data?.meta?.total_pages || 1;

  const handleExport = async () => {
    try {
      const response = await auditLogsApi.exportLogs({
        search: search || undefined,
        action: action || undefined,
        resource_type: resourceType || undefined,
        level: level || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      const blob = new Blob([response.data as any], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Audit logs exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const levelBadge = (lvl: string) => {
    switch (lvl) {
      case 'error':
        return 'bg-red-500/20 text-red-400';
      case 'warning':
        return 'bg-amber-500/20 text-amber-400';
      default:
        return 'bg-teal-500/20 text-teal-400';
    }
  };

  const resetFilters = () => {
    setSearch('');
    setAction('');
    setResourceType('');
    setLevel('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Audit Logs</h1>
          <p className="text-sm text-white/50 mt-1">Track all system activities and changes</p>
        </div>
        <button onClick={handleExport} className="btn-outline flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
          <Download className="w-4 h-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card-dark p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Filter className="w-4 h-4 text-white/50" />
          <span className="text-sm font-medium text-white/70">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search..."
              className="input-dark w-full pl-9"
            />
          </div>
          <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className="input-dark">
            <option value="">All Actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
            <option value="export">Export</option>
          </select>
          <select value={resourceType} onChange={(e) => { setResourceType(e.target.value); setPage(1); }} className="input-dark">
            <option value="">All Resources</option>
            <option value="exam">Exam</option>
            <option value="user">User</option>
            <option value="question">Question</option>
            <option value="attempt">Attempt</option>
            <option value="enrollment">Enrollment</option>
            <option value="settings">Settings</option>
          </select>
          <select value={level} onChange={(e) => { setLevel(e.target.value); setPage(1); }} className="input-dark">
            <option value="">All Levels</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="input-dark w-full pl-9"
              placeholder="From date"
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="input-dark w-full pl-9"
              placeholder="To date"
            />
          </div>
          <button onClick={resetFilters} className="btn-outline px-4 py-2 rounded-lg text-sm">
            Clear Filters
          </button>
        </div>
      </div>

      {/* Logs table */}
      <div className="card-dark overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-white/50">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <ScrollText className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No audit logs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Timestamp</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">User</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Action</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Resource</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Level</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">IP Address</th>
                  <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Description</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                    <td className="p-3 text-xs text-white/70 whitespace-nowrap">
                      {log.timestamp ? format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss') : '—'}
                    </td>
                    <td className="p-3">
                      <p className="text-sm text-white/90">{log.user_name}</p>
                      <p className="text-xs text-white/50">{log.user_email}</p>
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-white/[0.06] text-white/80 capitalize">{log.action}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-white/80 capitalize">{log.resource_type}</span>
                      {log.resource_id && (
                        <p className="text-xs text-white/40 font-mono truncate max-w-[120px]">{log.resource_id}</p>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${levelBadge(log.level)}`}>{log.level}</span>
                    </td>
                    <td className="p-3 text-xs text-white/60 font-mono">{log.ip_address}</td>
                    <td className="p-3 text-sm text-white/60 max-w-[300px] truncate">{log.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-white/[0.06]">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-outline px-3 py-1 text-sm rounded disabled:opacity-50">
              Previous
            </button>
            <span className="text-sm text-white/60">Page {page} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-outline px-3 py-1 text-sm rounded disabled:opacity-50">
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
